import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  useMyTeam,
  useTeamsByStore,
} from "@coworker/app/src/core/hooks/useStoreTeams";
import { useActiveStoreUsers } from "@coworker/apprestructured/src/shared/hooks/users/useActiveStoreUsers";
import FixaCheckbox from "@coworker/apprestructured/src/shared/wrappers/FixaCheckbox/FixaCheckbox";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { OnConfirmClickResult } from "@coworker/apprestructured/src/task-management/components/AssignTo/AssignToModal";
import { memo, useCallback, useMemo, useState } from "react";
import { AssignTo } from "../../../../components/AssignTo/AssignTo";
import Actions from "./Actions/Actions";
import Demo from "./Demo/Demo";
import Highlight from "./Highlight/Highlight";
import Name from "./Name/Name";

function AssignToDemo() {
  const [assignedTeamId, setAssignedTeamId] = useState<string | null>(null);
  const [assignedUserId, setAssignedUserId] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const storeId = useStoreId();

  const { data: teams, isLoading: isTeamsLoading } = useTeamsByStore(
    storeId || ""
  );

  const { data: activeUsers, isLoading: isUsersLoading } =
    useActiveStoreUsers();
  const myTeam = useMyTeam();

  const assignableTeams = useMemo(() => {
    return teams?.filter((team) => team.status === "ACTIVE");
  }, [teams]);

  const description = useMemo(() => {
    return assignedUserId
      ? (activeUsers || []).find((user) => user.uid === assignedUserId)
          ?.fullName || ""
      : assignedTeamId
        ? teams?.find((team) => team.id === assignedTeamId)?.name || ""
        : "";
  }, [activeUsers, assignedTeamId, assignedUserId, teams]);

  const handleConfirmClick = useCallback(
    ({ teamId, userId }: OnConfirmClickResult) => {
      setAssignedTeamId(teamId);
      setAssignedUserId(userId);
    },
    []
  );

  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const isError =
    showValidation && assignedTeamId === null && assignedUserId === null;

  return (
    <Demo>
      <Name>AssignTo</Name>
      <Highlight>
        <FixaListView id="assigntodemo" size="small">
          <AssignTo
            assignedTeamId={assignedTeamId}
            assignedUserId={assignedUserId}
            disabled={disabled}
            description={description}
            searchQuery={searchQuery}
            isTeamsLoading={isTeamsLoading}
            myTeamId={myTeam?.id || ""}
            isUsersLoading={isUsersLoading}
            teams={assignableTeams || []}
            users={activeUsers || []}
            isError={isError}
            onConfirmClick={handleConfirmClick}
            onSearchQueryChange={handleSearchQueryChange}
          />
        </FixaListView>
      </Highlight>
      <Actions>
        <FixaCheckbox
          label="disabled"
          value="assigntodemo-disabled"
          id="assigntodemo-disabled"
          checked={disabled}
          onChange={() => setDisabled((prev) => !prev)}
        />
        <FixaCheckbox
          label="showValidation"
          value="assigntodemo-showValidation"
          id="assigntodemo-showValidation"
          checked={showValidation}
          onChange={() => setShowValidation((prev) => !prev)}
        />
      </Actions>
    </Demo>
  );
}

export default memo(AssignToDemo);
